import { useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { HelpCircle, Check, X, RotateCcw, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { AnimatedCounter } from '@/components/motion';
import type { QuizSet } from '../data/quiz-data';

interface QuickQuizProps {
  quiz: QuizSet;
  className?: string;
}

export function QuickQuiz({ quiz, className }: QuickQuizProps) {
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(boolean | null)[]>(() => quiz.questions.map(() => null));
  const [showResult, setShowResult] = useState(false);

  const question = quiz.questions[currentQ];
  const isAnswered = selected !== null;
  const isCorrect = selected === question?.correctIndex;
  const totalCorrect = answers.filter((a) => a === true).length;
  const handleSelect = useCallback(
    (index: number) => {
      if (isAnswered) return;
      setSelected(index);
      setAnswers((prev) => {
        const next = [...prev];
        next[currentQ] = index === question.correctIndex;
        return next;
      });
    },
    [isAnswered, currentQ, question]
  );

  const handleNext = () => {
    if (currentQ < quiz.questions.length - 1) {
      setCurrentQ((c) => c + 1);
      setSelected(null);
    } else {
      setShowResult(true);
    }
  };

  const handleReset = () => {
    setCurrentQ(0);
    setSelected(null);
    setAnswers(quiz.questions.map(() => null));
    setShowResult(false);
  };

  // Final score screen
  if (showResult) {
    const pct = Math.round((totalCorrect / quiz.questions.length) * 100);
    return (
      <Card className={cn('overflow-hidden', className)}>
        <div className="h-1 bg-gradient-to-r from-violet-400 to-indigo-500" />
        <CardContent className="p-6 text-center space-y-4">
          <Award
            className={cn(
              'w-12 h-12 mx-auto',
              pct >= 70 ? 'text-yellow-500' : 'text-muted-foreground'
            )}
          />
          <div>
            <p className="text-lg font-bold">
              <AnimatedCounter target={totalCorrect} duration={0.8} /> / {quiz.questions.length}
            </p>
            <p className="text-sm text-muted-foreground">
              {pct >= 100
                ? '¡Perfecto! Dominas el tema.'
                : pct >= 70
                  ? '¡Muy bien! Tienes un buen entendimiento.'
                  : 'Repasa el capítulo e intenta de nuevo.'}
            </p>
          </div>
          <Button variant="outline" size="sm" className="gap-1" onClick={handleReset}>
            <RotateCcw className="w-3 h-3" />
            Intentar de nuevo
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <div className="h-1 bg-gradient-to-r from-violet-400 to-indigo-500" />
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold">Quiz: {quiz.title}</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {currentQ + 1} / {quiz.questions.length}
          </Badge>
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQ}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <p className="text-sm font-medium mb-3">{question.question}</p>

            {/* Options */}
            <div className="space-y-2">
              {question.options.map((option, i) => {
                const isThis = selected === i;
                const isCorrectOption = i === question.correctIndex;
                let optionStyle =
                  'border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer';

                if (isAnswered) {
                  if (isCorrectOption) {
                    optionStyle = 'border-green-400 bg-green-50';
                  } else if (isThis && !isCorrect) {
                    optionStyle = 'border-red-400 bg-red-50';
                  } else {
                    optionStyle = 'border-border opacity-50 cursor-default';
                  }
                }

                return (
                  <button
                    key={i}
                    type="button"
                    className={cn(
                      'w-full text-left rounded-lg border p-3 text-sm transition-all flex items-center gap-2',
                      optionStyle
                    )}
                    onClick={() => handleSelect(i)}
                    disabled={isAnswered}
                  >
                    <span
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
                        isAnswered && isCorrectOption
                          ? 'bg-green-500 text-white'
                          : isAnswered && isThis && !isCorrect
                            ? 'bg-red-500 text-white'
                            : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {isAnswered && isCorrectOption ? (
                        <Check className="w-3 h-3" />
                      ) : isAnswered && isThis && !isCorrect ? (
                        <X className="w-3 h-3" />
                      ) : (
                        String.fromCharCode(65 + i) // A, B, C, D
                      )}
                    </span>
                    <span>{option}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Feedback & Next */}
        <AnimatePresence>
          {isAnswered && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div
                className={cn(
                  'rounded-md p-3 text-xs',
                  isCorrect
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-amber-50 border border-amber-200'
                )}
              >
                <p
                  className={cn(
                    'font-semibold mb-1',
                    isCorrect ? 'text-green-700' : 'text-amber-700'
                  )}
                >
                  {isCorrect ? '¡Correcto!' : 'No exactamente.'}
                </p>
                <p className={cn(isCorrect ? 'text-green-600' : 'text-amber-600')}>
                  {question.explanation}
                </p>
              </div>
              <div className="mt-3 flex justify-end">
                <Button size="sm" className="text-xs" onClick={handleNext}>
                  {currentQ < quiz.questions.length - 1 ? 'Siguiente →' : 'Ver Resultado'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
